import Information16 from '@carbon/icons-react/es/information/16';
import { Button, Tooltip } from 'antd';
import { memoize } from 'lodash';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectTemplateSpaceData } from 'data/templates/projects';
import { useProjectDynamicTemplateConditions } from 'data/templates/projects/hooks/use-project-dynamic-template';
import { useTranslation } from 'hooks/use-translation';
import { DynamicConditionsRow } from './dynamic-conditons-row';

const LinkButton = styled(Button)`
  height: inherit;
`;

const StyledEmptyState = styled.div`
  padding-left: var(--spacing-05);
  color: var(--gray-50);
`;

const StyledRow = styled.div`
  padding: var(--spacing-03) var(--spacing-05);
  height: 40px;
`;

export const useDynamicConditionsColumn = (
  onAddConditionsClick: (record) => void,
  onEditConditionsClick: (record: any, recordCondition: any) => void,
  onDeleteConditionsConfirm: (record) => void,
  selectedRecord?: ProjectTemplateSpaceData,
) => {
  const dynamicConditons = useProjectDynamicTemplateConditions();

  const _onEditClick = useMemo(
    () =>
      memoize(
        (record, recordCondition) => () =>
          onEditConditionsClick(record, recordCondition),
      ),
    [onEditConditionsClick],
  );

  const _onDeleteConfirmClick = useMemo(
    () => memoize((record) => () => onDeleteConditionsConfirm(record)),
    [onDeleteConditionsConfirm],
  );

  return {
    className: 'dynamic-conditions-column',
    title: (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: 'var(--spacing-03) var(--spacing-05)',
        }}
      >
        <span style={{ marginRight: '5px' }}>
          {useTranslation('templates.dynamic_conditions')}
        </span>
        <Tooltip
          placement="right"
          title={useTranslation('templates.dynamic_conditions.tooltip')}
        >
          <Information16 />
        </Tooltip>
      </div>
    ),
    dataIndex: 'conditions',
    key: 'conditions',
    render: (text, record) => {
      const recordCondition = dynamicConditons?.[record.id];
      const hierarchyLevel = record.level as number;

      return (
        <StyledRow
          className={
            record.type === 'SPACE_TAB' && record.private ? 'private' : ''
          }
        >
          {recordCondition ? (
            <DynamicConditionsRow
              hierarchyLevel={hierarchyLevel}
              id={record.id}
              record={record}
              filter={recordCondition}
              onEditClick={_onEditClick(record, recordCondition)}
              onDeleteConfirmClick={_onDeleteConfirmClick(record)}
              selectedRecord={selectedRecord}
            />
          ) : record.conditionRequired === false ? (
            <StyledEmptyState>
              <FormattedMessage id="templates.dynamic_no_conditions.state_text" />
            </StyledEmptyState>
          ) : (
            <StyledEmptyState>
              <FormattedMessage id="templates.dynamic_conditions.empty_state_text" />
              <LinkButton
                type="link"
                onClick={() => {
                  onAddConditionsClick(record);
                }}
              >
                <FormattedMessage id="templates.dynamic_conditions.empty_state_link" />
              </LinkButton>
            </StyledEmptyState>
          )}
        </StyledRow>
      );
    },
  };
};
